import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import UpgradePlanModal, { UPGRADE_PLANS } from 'components/shared/UpgradePlanModal';
import axiosPrivate from 'config/axiosPrivate';

import './Forfait.css';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_FORFAIT_PAGE = '/AdminClient/Forfait';

const Check = () => <span className="forfait-v2-check">✓</span>;

const EngineTag = ({ children }) => (
  <span className="forfait-v2-engine-tag">{children}</span>
);

const Forfait = () => {
  usePageTracking(ADMIN_CLIENT_FORFAIT_PAGE);

  const { t } = useTranslation();
  const history = useHistory();
  const [upgradePlanKey, setUpgradePlanKey] = useState(null);
  const selectedUpgradePlan = upgradePlanKey ? UPGRADE_PLANS[upgradePlanKey] : null;
  const [companyQuotas, setCompanyQuotas] = useState(null);

  const openUpgrade = (planKey) => setUpgradePlanKey(planKey);
  const closeUpgrade = () => setUpgradePlanKey(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (isMounted) setCompanyQuotas(data || null);
      } catch (e) {
        console.error('Erreur récupération company quotas:', e);
      }
    };

    fetchCompanyQuotas();

    return () => {
      isMounted = false;
    };
  }, []);

  const normalizePlanKey = (rawKey) => {
    const v = String(rawKey || '').trim().toLowerCase();
    if (!v) return '';
    if (v === 'gratuit' || v === 'free') return 'free';
    if (v === 'pro') return 'pro';
    if (v === 'business') return 'business';
    if (v === 'entreprise' || v === 'enterprise') return 'entreprise';
    if (v === 'custom') return 'custom';
    // legacy mappings
    if (v === 'light') return 'pro';
    if (v === 'premium') return 'business';
    return '';
  };

  const currentPlanKey = normalizePlanKey(companyQuotas?.planKey) || null;
  const tierOrder = { free: 0, pro: 1, business: 2, entreprise: 3 };
  const isNextUpgrade = (targetPlanKey) =>
    currentPlanKey &&
    Object.prototype.hasOwnProperty.call(tierOrder, targetPlanKey) &&
    Object.prototype.hasOwnProperty.call(tierOrder, currentPlanKey) &&
    tierOrder[targetPlanKey] === tierOrder[currentPlanKey] + 1;
  const shouldShowPlanAction = (planKey) => {
    if (!currentPlanKey) return true;
    if (currentPlanKey === planKey) return true;
    if (
      Object.prototype.hasOwnProperty.call(tierOrder, planKey) &&
      Object.prototype.hasOwnProperty.call(tierOrder, currentPlanKey)
    ) {
      return tierOrder[planKey] > tierOrder[currentPlanKey];
    }
    return true;
  };
  const getFeatureColumnClass = (planKey) =>
    currentPlanKey === planKey ? 'forfait-v2-col--current' : '';

  const renderPlanCta = (planKey) => {
    if (!shouldShowPlanAction(planKey)) return null;
    const isCurrent = currentPlanKey === planKey;
    const isPrimary = isNextUpgrade(planKey);

    if (planKey === 'custom') {
      return (
        <button
          type="button"
          className={`forfait-v2-cta${isCurrent ? ' forfait-v2-cta--disabled' : ''}`}
          disabled={isCurrent}
          onClick={() => history.push('/AdminClient/Support')}
        >
          {isCurrent ? t('Forfait actuel') : t('Demander un devis')}
        </button>
      );
    }

    return (
      <button
        type="button"
        className={`forfait-v2-cta${isPrimary ? ' forfait-v2-cta--primary' : ''}${isCurrent ? ' forfait-v2-cta--disabled' : ''}`}
        disabled={isCurrent}
        onClick={() => openUpgrade(planKey)}
      >
        {isCurrent ? t('Forfait actuel') : t("S'abonner")}
      </button>
    );
  };

  return (
    <div className="forfait-page">
      <div className="forfait-header">
        <button
          type="button"
          className="forfait-back"
          aria-label="Retour"
          onClick={() => history.push('/AdminClient/Abonnement')}
        >
          <img src="/Images/back.png" alt="" className="forfait-back-icon" />
          <span> {t('Abonnement')}</span>
        </button>
      </div>

      <div className="forfait-header-content">
        <div className="forfait-title-row">
          <button
            type="button"
            className="forfait-title-link"
            onClick={() => history.push('/AdminClient/Abonnement')}
            aria-label="Aller à la page subscription"
          >
            {t('Abonnement')}
          </button>
          <div className="forfait-mode-pill">{t('Par site (multi-sites possibles)')}</div>
        </div>
        <div className="forfait-subtitle">
          {t('Choisissez la formule adaptée à vos besoins et faites évoluer votre usage à tout moment.')}
          <br />
          {t('Besoin d’un plan personnalisé ?')}{' '}
          <a href="/AdminClient/Support" className="forfait-link-inline">
            {t('Contactez nous')}
          </a>
        </div>
      </div>

      <div className="forfait-v2">
        <div className="forfait-v2-cards">
          {/* PRO */}
          <div className={`forfait-v2-card${currentPlanKey === 'pro' ? ' forfait-v2-card--current' : ''}`}>
            <div className="forfait-v2-card-head">
              <div className="forfait-v2-card-name">PRO</div>
              <div className="forfait-v2-card-tagline">{t('Pour produire efficacement')}</div>
            </div>
            <div className="forfait-v2-card-price-block">
              <div className="forfait-v2-card-price">
                129€<span className="forfait-v2-card-price-meta"> / {t('mois')}</span>
              </div>
              <div className="forfait-v2-card-price-note">{t('Mensuel, sans engagement')}</div>
              <div className="forfait-v2-card-price-note">{t('Soit 1 548 € facturés / an')}</div>
            </div>
            <div className="forfait-v2-card-audience">
              {t('Freelancers, petites équipes marketing/SEO')}
            </div>
            <div className="forfait-v2-package">
              <div className="forfait-v2-package-title">{t('Inclus dans le package')}</div>
              <div className="forfait-v2-package-grid">
                <b>1</b><span>{t('workspace (site web)')}</span>
                <b>2</b><span>{t('utilisateurs')}</span>
                <b>2</b><span>{t('agents IA')}</span>
                <b>20</b><span>{t('contenus / mois')}</span>
                <b>200</b><span>{t('crédits IA / mois')}</span>
                <b className="forfait-v2-ok">✓</b><span>{t('Onboarding')}</span>
              </div>
              <div className="forfait-v2-engines">
                <div className="forfait-v2-package-title">{t('Moteur de rédaction')}</div>
                <div className="forfait-v2-engines-row">
                  <EngineTag>GPT</EngineTag>
                </div>
              </div>
            </div>
            <div className="forfait-v2-features">
              <div><Check />{t('Contenu Web + SEO')}</div>
              <div><Check />{t('Score SEO & UX')}</div>
              <div><Check />{t('GEO — base')}</div>
              <div><Check />{t('Publication auto sur WordPress')}</div>
            </div>
            {renderPlanCta('pro')}
          </div>

          {/* BUSINESS */}
          <div className={`forfait-v2-card forfait-v2-card--popular${currentPlanKey === 'business' ? ' forfait-v2-card--current' : ''}`}>
            <span className="forfait-v2-popular-badge">{t('Le plus populaire')}</span>
            <div className="forfait-v2-card-head">
              <div className="forfait-v2-card-name">BUSINESS</div>
              <div className="forfait-v2-card-tagline forfait-v2-card-tagline--accent">
                {t('Pour industrialiser sa production')}
              </div>
            </div>
            <div className="forfait-v2-card-price-block">
              <div className="forfait-v2-card-price">
                369€<span className="forfait-v2-card-price-meta"> / {t('mois')}</span>
              </div>
              <div className="forfait-v2-card-price-note">{t('Mensuel, sans engagement')}</div>
              <div className="forfait-v2-card-price-note">{t('Soit 4 428 € facturés / an')}</div>
            </div>
            <div className="forfait-v2-card-audience">
              {t('Équipes SEO, marketing et contenu qui produisent régulièrement')}
            </div>
            <div className="forfait-v2-package forfait-v2-package--accent">
              <div className="forfait-v2-package-title forfait-v2-package-title--accent">
                {t('Inclus dans le package')}
              </div>
              <div className="forfait-v2-package-grid forfait-v2-package-grid--accent">
                <b>2</b><span>{t('workspaces (sites web)')}</span>
                <b>4</b><span>{t('utilisateurs')}</span>
                <b>3</b><span>{t('agents IA')}</span>
                <b>50</b><span>{t('contenus / mois')}</span>
                <b>500</b><span>{t('crédits IA / mois')}</span>
                <b className="forfait-v2-ok">✓</b><span>{t('Onboarding')}</span>
              </div>
              <div className="forfait-v2-engines forfait-v2-engines--accent">
                <div className="forfait-v2-package-title forfait-v2-package-title--accent">
                  {t('Moteur de rédaction')}
                </div>
                <div className="forfait-v2-engines-row">
                  <EngineTag>GPT</EngineTag>
                  <EngineTag>Gemini</EngineTag>
                </div>
              </div>
            </div>
            <div className="forfait-v2-features">
              <div className="forfait-v2-features-muted"><Check />{t('Tout PRO, plus :')}</div>
              <div><Check />{t('GEO avancée')}</div>
              <div><Check />{t('Mots-clés, briefs & gap sémantique')}</div>
              <div><Check />{t('Enrichissement SEO')}</div>
              <div><Check />{t("Collaboration et workflow d'équipe")}</div>
              <div className="forfait-v2-option-row">
                <span className="forfait-v2-option-badge">{t('En option')}</span>
                {t('Traduction multilingue IA')}
              </div>
            </div>
            {renderPlanCta('business')}
          </div>

          {/* ENTREPRISE */}
          <div className={`forfait-v2-card${currentPlanKey === 'entreprise' ? ' forfait-v2-card--current' : ''}`}>
            <div className="forfait-v2-card-head">
              <div className="forfait-v2-card-name">ENTREPRISE</div>
              <div className="forfait-v2-card-tagline">{t("Pour déployer à l'échelle")}</div>
            </div>
            <div className="forfait-v2-card-price-block">
              <div className="forfait-v2-card-price">
                579€<span className="forfait-v2-card-price-meta"> / {t('mois')}</span>
              </div>
              <div className="forfait-v2-card-price-note">{t('Mensuel, sans engagement')}</div>
              <div className="forfait-v2-card-price-note">{t('Soit 6 948 € facturés / an')}</div>
            </div>
            <div className="forfait-v2-card-audience">
              {t('Agences, grands comptes, groupes, multi-marques / multi-sites')}
            </div>
            <div className="forfait-v2-package">
              <div className="forfait-v2-package-title">{t('Inclus dans le package')}</div>
              <div className="forfait-v2-package-grid">
                <b>4</b><span>{t('workspaces (sites web)')}</span>
                <b>8</b><span>{t('utilisateurs')}</span>
                <b>5</b><span>{t('agents IA')}</span>
                <b>100</b><span>{t('contenus / mois')}</span>
                <b>1 000</b><span>{t('crédits IA / mois')}</span>
                <b className="forfait-v2-ok">✓</b><span>{t('Onboarding')}</span>
              </div>
              <div className="forfait-v2-engines">
                <div className="forfait-v2-package-title">{t('Moteur de rédaction')}</div>
                <div className="forfait-v2-engines-row">
                  <EngineTag>GPT</EngineTag>
                  <EngineTag>Gemini</EngineTag>
                  <EngineTag>Claude</EngineTag>
                  <EngineTag>Perplexity</EngineTag>
                  <EngineTag>{t('Multi-modèles')}</EngineTag>
                </div>
              </div>
            </div>
            <div className="forfait-v2-features">
              <div className="forfait-v2-features-muted"><Check />{t('Tout BUSINESS, plus :')}</div>
              <div><Check />{t('GEO avancée + multi-modèles')}</div>
              <div><Check />{t('Contenu Social (fiche YouTube)')}</div>
              <div><Check />{t('Génération augmentée (RAG)')}</div>
              <div><Check />{t('Support prioritaire')}</div>
              <div className="forfait-v2-option-row">
                <span className="forfait-v2-option-badge">{t('En option')}</span>
                {t('Traduction multilingue IA')}
              </div>
            </div>
            {renderPlanCta('entreprise')}
          </div>

          {/* CUSTOM */}
          <div className={`forfait-v2-card${currentPlanKey === 'custom' ? ' forfait-v2-card--current' : ''}`}>
            <div className="forfait-v2-card-head">
              <div className="forfait-v2-card-name">CUSTOM</div>
              <div className="forfait-v2-card-tagline">{t('Besoins spécifiques')}</div>
            </div>
            <div className="forfait-v2-card-price forfait-v2-card-price--custom">
              {t('Sur mesure')}
            </div>
            <div className="forfait-v2-features forfait-v2-features--bordered">
              <div><Check />{t('Tarification adaptée')}</div>
              <div><Check />{t('Volumétrie importante')}</div>
              <div><Check />{t('Accompagnement dédié')}</div>
            </div>
            {renderPlanCta('custom')}
          </div>
        </div>

        <h2 className="forfait-v2-compare-title">{t('Comparatif détaillé')}</h2>
        <div className="forfait-v2-table-wrap">
          <table className="forfait-v2-table">
            <thead>
              <tr>
                <th className="forfait-v2-th-feature">{t('Fonctionnalité')}</th>
                <th className={getFeatureColumnClass('pro')}>
                  PRO
                  <div className="forfait-v2-th-price">129 € / {t('mois')}</div>
                </th>
                <th className={`forfait-v2-th-business ${getFeatureColumnClass('business')}`}>
                  BUSINESS
                  <div className="forfait-v2-th-price forfait-v2-th-price--accent">369 € / {t('mois')}</div>
                </th>
                <th className={getFeatureColumnClass('entreprise')}>
                  ENTREPRISE
                  <div className="forfait-v2-th-price">579 € / {t('mois')}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th colSpan={4} className="forfait-v2-section forfait-v2-section--accent">
                  {t('Inclus dans le package')}
                </th>
              </tr>
              <tr className="forfait-v2-row-soft">
                <th>{t('Workspaces (site web)')}</th>
                <td className={getFeatureColumnClass('pro')}>1</td>
                <td className={`forfait-v2-td-business ${getFeatureColumnClass('business')}`}>2</td>
                <td className={getFeatureColumnClass('entreprise')}>4</td>
              </tr>
              <tr className="forfait-v2-row-soft">
                <th>{t('Utilisateurs inclus')}</th>
                <td className={getFeatureColumnClass('pro')}>2</td>
                <td className={`forfait-v2-td-business ${getFeatureColumnClass('business')}`}>4</td>
                <td className={getFeatureColumnClass('entreprise')}>8</td>
              </tr>
              <tr className="forfait-v2-row-soft">
                <th>{t('Agents IA')}</th>
                <td className={getFeatureColumnClass('pro')}>2</td>
                <td className={`forfait-v2-td-business ${getFeatureColumnClass('business')}`}>3</td>
                <td className={getFeatureColumnClass('entreprise')}>5</td>
              </tr>
              <tr className="forfait-v2-row-soft">
                <th>{t('Contenus / mois')}</th>
                <td className={getFeatureColumnClass('pro')}>20</td>
                <td className={`forfait-v2-td-business ${getFeatureColumnClass('business')}`}>50</td>
                <td className={getFeatureColumnClass('entreprise')}>100</td>
              </tr>
              <tr className="forfait-v2-row-soft">
                <th>{t('Crédits IA / mois')}</th>
                <td className={getFeatureColumnClass('pro')}>200</td>
                <td className={`forfait-v2-td-business ${getFeatureColumnClass('business')}`}>500</td>
                <td className={getFeatureColumnClass('entreprise')}>1 000</td>
              </tr>
              <tr className="forfait-v2-row-soft">
                <th>{t('Moteur de rédaction')}</th>
                <td className={getFeatureColumnClass('pro')}>GPT</td>
                <td className={`forfait-v2-td-business ${getFeatureColumnClass('business')}`}>GPT, Gemini</td>
                <td className={getFeatureColumnClass('entreprise')}>GPT, Gemini, Claude, Perplexity</td>
              </tr>
              <tr className="forfait-v2-row-soft forfait-v2-row-soft--last">
                <th>{t('Onboarding')}</th>
                <td className={`forfait-v2-ok ${getFeatureColumnClass('pro')}`}>✓</td>
                <td className={`forfait-v2-ok forfait-v2-td-business ${getFeatureColumnClass('business')}`}>✓</td>
                <td className={`forfait-v2-ok ${getFeatureColumnClass('entreprise')}`}>✓</td>
              </tr>

              <tr className="forfait-v2-sep" aria-hidden="true">
                <td colSpan={4} />
              </tr>

              <tr>
                <th colSpan={4} className="forfait-v2-section">
                  {t('Fonctionnalités')}
                </th>
              </tr>
              <tr>
                <th>{t('Optimisation pour les moteurs IA (GEO)')}</th>
                <td className={getFeatureColumnClass('pro')}>{t('Base')}</td>
                <td className={`forfait-v2-td-business forfait-v2-td-accent ${getFeatureColumnClass('business')}`}>{t('Avancée')}</td>
                <td className={`forfait-v2-td-accent ${getFeatureColumnClass('entreprise')}`}>{t('Avancée + multi-modèles')}</td>
              </tr>
              {[
                'Contenu Web + SEO',
                'Score SEO & UX',
                "Assistant IA intégré à l'éditeur*",
                'Publication automatique sur WordPress',
                'Export multi-formats (Word, Excel & HTML)',
                'Gestion de projets éditoriaux',
              ].map((label) => (
                <tr key={label}>
                  <th>{t(label)}</th>
                  <td className={`forfait-v2-ok ${getFeatureColumnClass('pro')}`}>✓</td>
                  <td className={`forfait-v2-ok forfait-v2-td-business ${getFeatureColumnClass('business')}`}>✓</td>
                  <td className={`forfait-v2-ok ${getFeatureColumnClass('entreprise')}`}>✓</td>
                </tr>
              ))}
              {[
                'Découverte mots-clés',
                'Génération de briefs',
                'Analyse et comblement du gap sémantique',
                'Enrichissement SEO',
                "Collaboration et workflow d'équipe",
              ].map((label) => (
                <tr key={label}>
                  <th>{t(label)}</th>
                  <td className={`forfait-v2-dash ${getFeatureColumnClass('pro')}`}>—</td>
                  <td className={`forfait-v2-ok forfait-v2-td-business ${getFeatureColumnClass('business')}`}>✓</td>
                  <td className={`forfait-v2-ok ${getFeatureColumnClass('entreprise')}`}>✓</td>
                </tr>
              ))}
              {[
                'Rédaction multi-modèles IA avancée',
                'Social (fiche YouTube)',
                'Génération augmentée (RAG)',
                'Support prioritaire',
              ].map((label) => (
                <tr key={label}>
                  <th>{t(label)}</th>
                  <td className={`forfait-v2-dash ${getFeatureColumnClass('pro')}`}>—</td>
                  <td className={`forfait-v2-dash forfait-v2-td-business ${getFeatureColumnClass('business')}`}>—</td>
                  <td className={`forfait-v2-ok ${getFeatureColumnClass('entreprise')}`}>✓</td>
                </tr>
              ))}
              <tr>
                <th>{t('Traduction multilingue IA')}</th>
                <td className={`forfait-v2-dash ${getFeatureColumnClass('pro')}`}>—</td>
                <td className={`forfait-v2-td-business ${getFeatureColumnClass('business')}`}>
                  <span className="forfait-v2-option-badge">{t('En option')}</span>
                </td>
                <td className={getFeatureColumnClass('entreprise')}>
                  <span className="forfait-v2-option-badge">{t('En option')}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="forfait-v2-footnote">
          {t("* avec crédits. Des packs de crédits IA supplémentaires (+200 / +500 / +1 000) sont disponibles à la carte pour les gros volumes, sans changer de palier.")}
        </div>
      </div>

      <UpgradePlanModal
        open={!!selectedUpgradePlan}
        onClose={closeUpgrade}
        plan={selectedUpgradePlan}
        minimumSiteCount={Math.max(1, Number(companyQuotas?.maxWebsites ?? 1))}
      />
    </div>
  );
};

export default Forfait;
